import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Fragment, memo, SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAddressSearch from '@/hooks/useAddressSearch';
import CustomPopper from '@/pages/components/CustomPopper';
import AddressSearchOption from '@/pages/components/AddressSearchOption';

const StartSearch = memo(function StartSearch({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('landing-page');

  return (
    <Button
      data-cy="start-search"
      variant="contained"
      size="large"
      sx={{
        borderRadius: '8px',
        backgroundColor: 'white',
        color: '#A82425',
        textTransform: 'none',
        fontSize: '18px',
        '&:hover': {
          backgroundColor: '#f0f0f0'
        }
      }}
      onClick={onClick}
    >
      {t('hero.address-search.search-button')}
    </Button>
  );
});

function AddressSearch() {
  const { t, i18n } = useTranslation('landing-page');
  const navigate = useNavigate();

  const {
    searchQuery,
    searchOptions,
    enhancedSearchOptions,
    enhancedPerimeterList,
    isLoading,
    handleInputChange,
    handleOptionSelect,
    selectedAddress
  } = useAddressSearch();

  const language = i18n.language === 'de-CH' ? 'de' : 'fr';

  const handleAddressSearch = useCallback(() => {
    try {
      const encodedObject = encodeURIComponent(JSON.stringify(selectedAddress));
      if (selectedAddress) {
        navigate(`/${language}/app/step1?address=${encodedObject}`);
      }
    }
    catch (error) {
      console.error(error);
    }
  }, [selectedAddress, navigate, language]);

  const handlePerimeterOptionSelect = useCallback(
    (_event: SyntheticEvent, option: any | null) => {
      if (!option) return;
      if (option.group === 'perimeter' && option.lat && option.long) {
        const encodedObject = encodeURIComponent(
          JSON.stringify({
            id: option.id,
            label: option.label,
            lat: option.lat,
            long: option.long
          })
        );
        navigate(`/${language}/app/step1?perimeter=${encodedObject}`);

        return;
      }

      handleOptionSelect(_event, option);
    },
    [language]
  );

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: '50%', xl: '60%' }
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <Autocomplete
          data-cy="address-search"
          freeSolo
          value={searchQuery}
          options={[...enhancedSearchOptions, ...enhancedPerimeterList]}
          groupBy={(option) => option.group}
          filterOptions={(x) => x}
          onInputChange={handleInputChange}
          onChange={handlePerimeterOptionSelect}
          loading={isLoading}
          loadingText={t('hero.address-search.loading')}
          noOptionsText={t('hero.address-search.no-options')}
          selectOnFocus
          clearOnBlur
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8
              }
            }
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderColor: 'white',
              borderWidth: '2px',
              borderRadius: '8px',
              '& fieldset': {
                borderColor: 'white',
                borderWidth: '2px',
                borderRadius: 'inherit'
              },
              '&:hover fieldset': {
                borderColor: 'white'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'white'
              },
              '& input': {
                color: 'white'
              },
              '& .MuiInputLabel-root': {
                color: 'white'
              }
            },
            '&.Mui-expanded .MuiOutlinedInput-root': {
              borderRadius: '8px 8px 0 0',
              ...(searchQuery &&
                searchOptions.length === 0 && {
                  borderBottom: 'none'
                }),
              '& fieldset': {
                borderRadius: 'inherit',
                ...(searchQuery &&
                  searchOptions.length === 0 && {
                    borderBottom: 'none'
                  })
              }
            },
            '& .MuiAutocomplete-clearIndicator': {
              color: 'white'
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('hero.address-search.placeholder')}
              slotProps={{
                input: {
                  ...params.InputProps,
                  sx: {
                    '&::placeholder': {
                      color: 'white'
                    }
                  }
                }
              }}
              variant="outlined"
            />
          )}
          PopperComponent={(props) => CustomPopper(props)}
          renderGroup={(params) => (
            <Box
              key={params.group}
              sx={{ mt: params.group === 'perimeter' && searchQuery.length > 0 ? '24px' : 0 }}
            >
              <Box sx={{ my: '6px', p: '6px 16px' }}>
                <Typography sx={{ color: 'rgba(255, 137, 61, 1)' }} fontWeight={700}>
                  {params.group === 'perimeter'
                    ? t('app:address-search-perimeter-group-title')
                    : t('app:address-search-address-group-title')}
                </Typography>
              </Box>
              {params.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <Fragment key={option.id}>
              <AddressSearchOption props={props} option={option} />
            </Fragment>
          )}
        />
        <StartSearch onClick={handleAddressSearch} />
      </Stack>
    </Box>
  );
}

export default AddressSearch;
